import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
	api,
	ApiError,
	type McpHttpSkillConfig,
	type McpStdioSkillConfig,
	type PluginSkillConfig,
	type Skill,
} from "../lib/api.ts";

/**
 * Per-skill page — view + edit displayName/description, view config (with edit
 * for the simple cases), delete. All edits open PRs against skills-extra.json.
 */
export function SkillProfile() {
	const { key } = useParams<{ key: string }>();
	const navigate = useNavigate();
	const [skill, setSkill] = useState<Skill | null>(null);
	const [usageCount, setUsageCount] = useState(0);
	const [error, setError] = useState<string | null>(null);

	const [editing, setEditing] = useState(false);
	const [draftDisplayName, setDraftDisplayName] = useState("");
	const [draftDescription, setDraftDescription] = useState("");
	const [draftConfig, setDraftConfig] = useState<string>("");
	const [busy, setBusy] = useState(false);
	const [prUrl, setPrUrl] = useState<string | null>(null);

	useEffect(() => {
		if (!key) return;
		api
			.getSkill(key)
			.then((r) => {
				setSkill(r.skill);
				setUsageCount(r.usageCount);
			})
			.catch((err) => {
				if (err instanceof ApiError && err.status === 404) setError(`Skill "${key}" not found.`);
				else setError((err as Error).message);
			});
	}, [key]);

	function startEdit() {
		if (!skill) return;
		setDraftDisplayName(skill.displayName);
		setDraftDescription(skill.description ?? "");
		setDraftConfig(JSON.stringify(skill.config, null, 2));
		setError(null);
		setPrUrl(null);
		setEditing(true);
	}

	async function handleSave() {
		if (!skill || !key) return;
		setBusy(true);
		setError(null);
		try {
			let parsedConfig: McpHttpSkillConfig | McpStdioSkillConfig | PluginSkillConfig | undefined;
			if (draftConfig.trim() !== JSON.stringify(skill.config, null, 2).trim()) {
				parsedConfig = JSON.parse(draftConfig) as typeof parsedConfig;
			}
			const res = await api.updateSkill(key, {
				displayName: draftDisplayName !== skill.displayName ? draftDisplayName : undefined,
				description:
					draftDescription.trim() === (skill.description ?? "").trim()
						? undefined
						: draftDescription.trim() === ""
							? null
							: draftDescription,
				config: parsedConfig,
			});
			setPrUrl(res.htmlUrl);
		} catch (err) {
			if (err instanceof SyntaxError) setError(`Invalid config JSON: ${err.message}`);
			else setError((err as Error).message);
		} finally {
			setBusy(false);
		}
	}

	async function handleDelete() {
		if (!skill || !key) return;
		if (usageCount > 0) {
			setError(`Detach from ${usageCount} agent(s) first.`);
			return;
		}
		if (!confirm(`Delete skill "${skill.displayName}"? This opens a PR removing it from the registry.`)) {
			return;
		}
		setBusy(true);
		setError(null);
		try {
			const res = await api.deleteSkill(key);
			setPrUrl(res.htmlUrl);
		} catch (err) {
			setError((err as Error).message);
		} finally {
			setBusy(false);
		}
	}

	if (error && !skill) {
		return (
			<div className="px-6 py-8">
				<Link to="/skills" className="text-sm text-zinc-500 hover:text-zinc-300">
					← back to skills
				</Link>
				<div className="mt-4 p-3 border border-red-500/30 rounded bg-red-500/10 text-sm text-red-300">
					{error}
				</div>
			</div>
		);
	}

	if (!skill) return <div className="px-6 py-8 text-zinc-500">Loading…</div>;

	return (
		<div className="px-6 py-8 max-w-3xl mx-auto">
			<Link to="/skills" className="text-sm text-zinc-500 hover:text-zinc-300">
				← back to skills
			</Link>

			<div className="mt-4 flex items-baseline justify-between gap-3">
				<div>
					<h1 className="text-xl font-semibold">{skill.displayName}</h1>
					<div className="text-sm text-zinc-500 mt-1 font-mono">
						{skill.key} · {skill.kind} · {skill.origin}
					</div>
					<div className="text-xs text-zinc-500 mt-1">
						{usageCount === 0
							? "Currently unassigned."
							: `Used by ${usageCount} agent${usageCount === 1 ? "" : "s"}.`}
					</div>
				</div>
				{!editing && (
					<div className="flex gap-2">
						<button
							type="button"
							onClick={startEdit}
							className="text-xs px-3 py-1 rounded border border-zinc-700 hover:border-zinc-600 text-zinc-300"
						>
							Edit
						</button>
						<button
							type="button"
							onClick={handleDelete}
							disabled={busy || usageCount > 0}
							className="text-xs px-3 py-1 rounded border border-red-700/50 hover:border-red-500 disabled:opacity-40 disabled:cursor-not-allowed text-red-300"
							title={usageCount > 0 ? "Detach from agents first." : "Remove this skill from the registry."}
						>
							Delete
						</button>
					</div>
				)}
			</div>

			{skill.description && !editing && (
				<div className="mt-4 text-sm text-zinc-400">{skill.description}</div>
			)}

			{editing ? (
				<div className="mt-6 border border-zinc-800 rounded-lg bg-zinc-900/40 p-4 space-y-3">
					<div>
						<label className="text-xs text-zinc-500">Display name</label>
						<input
							type="text"
							value={draftDisplayName}
							onChange={(e) => setDraftDisplayName(e.target.value)}
							className="mt-1 w-full px-3 py-1.5 bg-terminal border border-zinc-700 rounded text-zinc-100 text-sm focus:outline-none focus:border-zinc-500"
							disabled={busy}
						/>
					</div>
					<div>
						<label className="text-xs text-zinc-500">Description</label>
						<textarea
							value={draftDescription}
							onChange={(e) => setDraftDescription(e.target.value)}
							rows={2}
							className="mt-1 w-full px-3 py-1.5 bg-terminal border border-zinc-700 rounded text-zinc-200 text-sm resize-y focus:outline-none focus:border-zinc-500"
							disabled={busy}
						/>
					</div>
					<div>
						<label className="text-xs text-zinc-500">Config (JSON)</label>
						<textarea
							value={draftConfig}
							onChange={(e) => setDraftConfig(e.target.value)}
							rows={12}
							className="mt-1 w-full px-3 py-1.5 bg-terminal border border-zinc-700 rounded text-zinc-200 text-sm font-mono resize-y focus:outline-none focus:border-zinc-500"
							disabled={busy}
							spellCheck={false}
						/>
					</div>
					{error && <div className="text-xs text-red-300">{error}</div>}
					{prUrl && (
						<div className="text-sm text-emerald-300">
							✓ PR opened:{" "}
							<a href={prUrl} target="_blank" rel="noreferrer" className="underline break-all">
								{prUrl}
							</a>
						</div>
					)}
					<div className="flex gap-2 pt-1">
						<button
							type="button"
							onClick={handleSave}
							disabled={busy}
							className="px-3 py-1.5 bg-accent-600 hover:bg-accent-500 disabled:bg-zinc-800 disabled:text-zinc-500 text-white text-sm font-medium rounded"
						>
							{busy ? "Opening PR…" : "Propose change (PR)"}
						</button>
						<button
							type="button"
							onClick={() => setEditing(false)}
							disabled={busy}
							className="px-3 py-1.5 border border-zinc-700 hover:border-zinc-600 text-zinc-300 text-sm rounded"
						>
							Close
						</button>
					</div>
				</div>
			) : (
				<section className="mt-6">
					<div className="text-sm font-medium text-zinc-300 mb-2">Config</div>
					<pre className="text-xs font-mono text-zinc-300 bg-terminal/70 border border-zinc-800 rounded p-3 overflow-x-auto whitespace-pre">
						{JSON.stringify(skill.config, null, 2)}
					</pre>
					{skill.containsSecret && (
						<div className="text-[11px] text-amber-400 mt-1">⚠ contains a literal secret value</div>
					)}
				</section>
			)}

			{prUrl && !editing && (
				<div className="mt-4 text-sm text-emerald-300">
					✓ PR opened:{" "}
					<a href={prUrl} target="_blank" rel="noreferrer" className="underline break-all">
						{prUrl}
					</a>
					<div className="text-xs text-zinc-500 mt-1">
						After merge + auto-deploy (~1 min) the change goes live.{" "}
						<button
							type="button"
							onClick={() => navigate("/skills")}
							className="underline hover:text-emerald-200"
						>
							Back to Skills
						</button>
					</div>
				</div>
			)}
		</div>
	);
}
